import { persistedStore } from "@/lib/stores/persisted-store";
import type { PremiumTypeSlug } from "../queries/premium-queries";

export interface PremiumColumnDef {
  key: string;
  label: string;
  defaultVisible: boolean;
  sortable?: boolean;
  align?: "left" | "center" | "right";
  types?: PremiumTypeSlug[];
}

export const PREMIUM_COLUMNS: PremiumColumnDef[] = [
  { key: "code", label: "Code", defaultVisible: true, sortable: true },
  { key: "group", label: "Group", defaultVisible: true, types: ["door_style", "wood_species", "finish_option"] },
  { key: "description", label: "Description", defaultVisible: true, sortable: true },
  { key: "price_pct", label: "Price / %", defaultVisible: true, sortable: true, align: "right" },
  { key: "export_name", label: "Export Name", defaultVisible: true, sortable: true },
  { key: "categories", label: "Categories", defaultVisible: true },
  { key: "finish_options", label: "Finish Options", defaultVisible: true, types: ["wood_species"] },
  { key: "wood_species", label: "Wood Species", defaultVisible: true, types: ["door_style"] },
  { key: "images", label: "Images", defaultVisible: false, types: ["door_style", "wood_species", "finish_option"] },
  { key: "tags", label: "Tags", defaultVisible: true, types: ["door_style", "wood_species", "finish_option"] },
];

interface State {
  codeFilter: string;
  descFilter: string;
  selectedCatIds: number[];
  page: number;
  pageSize: number;
  orderBy: string;
  visibleColumns: string[];
}

interface Actions {
  setCodeFilter: (v: string) => void;
  setDescFilter: (v: string) => void;
  setSelectedCatIds: (v: number[]) => void;
  toggleCat: (id: number) => void;
  setPage: (v: number) => void;
  setPageSize: (v: number) => void;
  setOrderBy: (v: string) => void;
  toggleColumn: (key: string) => void;
  resetFilters: () => void;
}

export type UsePremiumStore = ReturnType<typeof createStore>;

function createStore(premiumType: PremiumTypeSlug) {
  const availableColumns = PREMIUM_COLUMNS.filter(
    (c) => !c.types || c.types.includes(premiumType),
  );
  const defaultVisible = availableColumns
    .filter((c) => c.defaultVisible)
    .map((c) => c.key);

  const defaults: State = {
    codeFilter: "",
    descFilter: "",
    selectedCatIds: [],
    page: 1,
    pageSize: 25,
    orderBy: "code",
    visibleColumns: defaultVisible,
  };

  return persistedStore<State & Actions>(
    (set) => ({
      ...defaults,
      setCodeFilter: (codeFilter) => set({ codeFilter, page: 1 }),
      setDescFilter: (descFilter) => set({ descFilter, page: 1 }),
      setSelectedCatIds: (selectedCatIds) => set({ selectedCatIds, page: 1 }),
      toggleCat: (id) =>
        set((s) => ({
          selectedCatIds: s.selectedCatIds.includes(id)
            ? s.selectedCatIds.filter((x) => x !== id)
            : [...s.selectedCatIds, id],
          page: 1,
        })),
      setPage: (page) => set({ page }),
      setPageSize: (pageSize) => set({ pageSize, page: 1 }),
      setOrderBy: (orderBy) => set({ orderBy, page: 1 }),
      toggleColumn: (key) =>
        set((s) => ({
          visibleColumns: s.visibleColumns.includes(key)
            ? s.visibleColumns.filter((k) => k !== key)
            : [...s.visibleColumns, key],
        })),
      resetFilters: () => set(defaults),
    }),
    {
      name: `catalog-premiums-${premiumType}`,
      partialize: (s) => ({
        codeFilter: s.codeFilter,
        descFilter: s.descFilter,
        selectedCatIds: s.selectedCatIds,
        pageSize: s.pageSize,
        orderBy: s.orderBy,
        visibleColumns: s.visibleColumns,
      }),
    },
  );
}

const stores = new Map<PremiumTypeSlug, ReturnType<typeof createStore>>();

export function usePremiumStore(premiumType: PremiumTypeSlug) {
  let store = stores.get(premiumType);
  if (!store) {
    store = createStore(premiumType);
    stores.set(premiumType, store);
  }
  return store;
}

export function getAvailablePremiumColumns(premiumType: PremiumTypeSlug): PremiumColumnDef[] {
  return PREMIUM_COLUMNS.filter((c) => !c.types || c.types.includes(premiumType));
}
